"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/Button";

export default function NotFound() {
  const pathname = usePathname();
  return (
    <div className="relative min-h-[calc(100vh-160px)] flex items-center justify-center overflow-hidden bg-cb-bg">
      {/* Background layers */}
      <div className="absolute inset-0 bg-dot-grid opacity-20 pointer-events-none" />
      <div className="absolute inset-0 hero-gradient pointer-events-none" />

      {/* Ghost "404" watermark */}
      <span
        className="absolute select-none pointer-events-none font-bold font-mono text-[20rem] leading-none text-cb-violet/4 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap"
        aria-hidden="true"
      >
        404
      </span>

      {/* Content */}
      <div className="relative z-10 flex flex-col lg:flex-row items-center gap-16 px-4 sm:px-6 lg:px-8 max-w-5xl w-full mx-auto py-24">
        {/* Left: text */}
        <div className="flex-1 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cb-violet/20 bg-cb-violet/5 mb-6">
            <span className="text-xs font-mono font-medium text-cb-violet-text">HTTP 404</span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-cb-text mb-4 leading-tight">
            This page was never
            <br />
            <span className="text-gradient-violet">provisioned.</span>
          </h1>

          <p className="text-lg text-cb-muted leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
            The resource you&apos;re looking for doesn&apos;t exist in this environment.
            It may have been moved, deleted, or it was never deployed to begin with.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
            <Button size="lg" asChild>
              <Link href="/" className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                Back to home
              </Link>
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link href="/courses" className="flex items-center gap-2">
                Browse courses
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Right: terminal artwork */}
        <div className="flex-shrink-0 w-full max-w-sm">
          <div
            className="rounded-xl border border-cb-border bg-cb-surface overflow-hidden shadow-2xl shadow-black/50"
            style={{ boxShadow: "0 0 60px rgba(90, 74, 227, 0.08), 0 25px 50px rgba(0,0,0,0.5)" }}
          >
            {/* Title bar */}
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-cb-border bg-cb-surface-2">
              <div className="h-2.5 w-2.5 rounded-full bg-red-500/50" />
              <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/50" />
              <div className="h-2.5 w-2.5 rounded-full bg-green-500/50" />
              <span className="ml-2 text-xs font-mono text-cb-muted-2">terminal</span>
            </div>

            {/* Terminal body */}
            <div className="p-5 font-mono text-xs leading-relaxed space-y-1">
              <p>
                <span className="text-cb-violet-text">$</span>{" "}
                <span className="text-cb-muted">GET {pathname}</span>
              </p>
              <p className="text-cb-muted-2 pl-2">&gt; HTTP/1.1 <span className="text-red-400/80">404 Not Found</span></p>
              <p className="text-cb-muted-2 pl-2">&gt; x-region: eu-west-1</p>
              <p className="text-cb-muted-2 pl-2">&gt; x-request-id: cb-00000000</p>
              <br />
              <p className="text-cb-muted">{"{"}</p>
              <p className="text-cb-muted pl-4">
                <span className="text-cb-violet-text">&quot;statusCode&quot;</span>
                {": "}
                <span className="text-red-400/80">404</span>
                {","}
              </p>
              <p className="text-cb-muted pl-4">
                <span className="text-cb-violet-text">&quot;error&quot;</span>
                {": "}
                <span className="text-cb-success">&quot;NoSuchResource&quot;</span>
                {","}
              </p>
              <p className="text-cb-muted pl-4">
                <span className="text-cb-violet-text">&quot;message&quot;</span>
                {": "}
                <span className="text-cb-success">&quot;Resource not provisioned&quot;</span>
              </p>
              <p className="text-cb-muted">{"}"}</p>
              <br />
              <p>
                <span className="text-cb-violet-text">$</span>{" "}
                <span className="text-cb-muted-2 animate-pulse">▌</span>
              </p>
            </div>
          </div>

          {/* Decorative dots */}
          <div className="flex justify-end gap-1.5 mt-3 pr-2">
            {[0.4, 0.25, 0.15].map((o, i) => (
              <div
                key={i}
                className="h-1 w-1 rounded-full bg-cb-violet"
                style={{ opacity: o }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
